import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import { SettingsManager } from './settings-manager';

/*
 * Interface that represents expected return form start endpoint in pay.nl
 */
export interface PayNlStartReponse {
  /**
   * Object with information on the result of the request
   */
  request: {
    /**
     * 0: request unsuccessful, 1: request successful
     */
    result: '0' | '1';
    /**
     * If an error occurred, you can find the error ID here
     */
    errorId?: string;
    /**
     *	If an error occurred, you can find the description here
     */
    errorMessage?: string;
  };

  /**
   * Object with information on the end user
   */
  endUser: {
    /**
     * 0: end user is not on blacklist, 1: end user is on blacklist
     */
    blacklist: '0' | '1';
  };

  /**
   * Object with information on the transaction
   */
  transaction: {
    /**
     *	Unique ID of the terminal transaction
     */
    transactionId: string;

    /**
     * URL to check the progress of a terminal transaction
     */
    paymentURL: string;

    /**
     * Indicates whether the payment screen can be loaded in a popup. 0: not allowed, 1: allowed
     */
    popupAllowed: boolean;

    /**
     * Payment reference of a transaction
     */
    paymentReference: string;
  };

  /**
   * Object with information on the terminal
   */
  terminal: {
    /**
     *	Unique transaction key that can be used to verify the status of the terminal payment or to generate a receipt.
     */
    hash: string;

    /**
     *	URL displaying the status of the Terminal transaction
     */
    statusUrl: string;

    /**
     * URL of the website displaying the status of the terminal transaction
     */
    cancelUrl: string;
  };
}

/**
 * Interface that represents expected return form transaction poll url in pay.nl
 */
export interface PayNlPollReponse {
  /**
   * Terminal status. Start: terminal is waiting for action by customer, final:
   * transaction has been completed (paid or cancelled), the terminal is once
   * more available.
   */
  status: string;

  /**
   * Unique reference of a terminal transaction
   */
  txid: string;

  /**
   * 0: receipt does not have to be signed 1: receipt should be signed by the customer
   */
  signature: string;

  /**
   * Shows which terminal the transaction was started on
   */
  terminal: string;

  /**
   * Unique reference of the terminal transaction
   */
  ssai: string;

  /**
   * Name of the card scheme used (such as Maestro, VISA)
   */
  cardbrandlabelname: string;

  /**
   * Identifier of the card scheme used
   */
  cardbrandidentifier: string;

  /**
   * Authorisation code of the terminal transaction
   */
  approvalID: string;

  /**
   * Base64 encoded receipt, use base64 decoding to display the image;
   */
  ticket: string;

  /**
   * 0: No error occurred - 1 : An error occurred during the payment process
   */
  error: '0' | '1';

  /**
   * Transaction amount in cents
   */
  amount: string;

  /**
   * This identifier is delivered via the terminal. If “status” : “final” and cancelled” : “1”, you can show this identifier in a message.
   */
  incidentcode: string;

  /**
   * This text is delivered via the terminal. If “status” : “final” and cancelled” : “1”, you can show this text in a message.
   */
  incidentcodetext: string;

  /**
   * 0: transaction was not cancelled 1: transaction was cancelled
   */
  cancelled: '0' | '1';

  /**
   * If "approved" : "1", you can generate the printed receipt using Base64
   * decode. You can also generate your own receipt using the following fields:
   * cardbrandlabelname, cardbrandidentifier, approvalID, txid, terminal
   */
  approved: '0' | '1';
}

/**
 * Service that expose pay nl endpoints to layers above it.
 */
@Injectable('payNlRequests')
export class PayNlRequests {
  private paymentOptionSubId: string;
  private serviceId: string;

  private;
  constructor(
    private $http: ng.IHttpService,
    private domain: Domain,
    private settingsManager: SettingsManager,
  ) {
    'ngInject';
  }

  /**
   * Call start method from pay.nl. {@link https://docs.pay.nl/developers?language=en#instore-start more}
   * @param amount
   * Quantity to be payed in the currency.
   * @param enduserEmailAddress
   * end use info, used mainly to integrate with loyyo
   * @param serviceId
   * should be set in tenant or user setting
   * @param paymentOptionId
   * ATM it's in-store payment, but docs reference what looks like dynamic api
   * https://admin.pay.nl/data/payment_profiles
   * @param paymentOptionSubId
   * should be set in user setting
   * @param ipAddress
   * dummy value, probably irrelevant in our case
   * @param finishUrl
   * dummy value, most likely we will never need it (at least not in in-store payments)
   * @return
   * Ng promise that resolve to the result of the query
   */
  start(
    amount: number,
    enduserEmailAddress: string,
    enduserPhoneNumber: string,
    tenantId: string,
    nr: string,
    bufferId: string,
    paymentOptionId = 1927,
    ipAddress: string = '127.0.0.1',
    finishUrl: string = 'https://api-dev.loyyo.nl/notifications/paynl',
  ): ng.IPromise<PayNlStartReponse> {
    return this.$http
      .post<PayNlStartReponse>(
        `${this.domain.getSpynlService()}/pay-nl`,
        {
          payNLPayload: {
            amount: Math.round(amount * 100),
            'enduser[emailAddress]': enduserEmailAddress,
            'enduser[phoneNumber]': enduserPhoneNumber,
            paymentOptionId,
            paymentOptionSubId: this.settingsManager.settings.payNLDeviceId,
            serviceId: this.settingsManager.settings.payNLStoreId,
            ipAddress,
            finishUrl,
            'transaction[orderExchangeUrl]': `https:${this.domain.getSpynlService()}/pay-nl-exchange`,
            'statsData[extra1]': tenantId,
            'statsData[extra2]': nr,
            'statsData[extra3]': bufferId,
            'transaction[description]': nr,
          },
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response) => response.data);
  }

  /**
   * Method to poll result of already started transaction
   * @param {string} url
   * Url to be queried
   * @return
   * Ng promise that resolves to the result of the query
   */
  poll(url: string): ng.IPromise<PayNlPollReponse> {
    return this.$http
      .get<PayNlPollReponse>(url)
      .then((response) => response.data);
  }
}
